import React, { createContext, useContext, createRef } from 'react';
import { createPortal } from 'react-dom';
import {
  INotificaitonCenterContext,
  TNotificationCenterProviderProps,
  TNotificationCenterProviderState,
  Callback,
  TNotificationContent,
  TNotificationOptions,
  TNotificationCenterConsumerProps,
} from '../types';
import { nullFn, generateUID } from '../utils';
import NotificationContainer from './NotificationContainer';
import { NotificationController } from './NotificationController';
import { Transition, TransitionGroup } from 'react-transition-group';

const NotificationCenterContext = createContext<
  INotificaitonCenterContext | undefined
>(undefined);
NotificationCenterContext.displayName = 'NotificationCenterContext';

const { Provider, Consumer } = NotificationCenterContext;

const canUseDOM = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

// SECTION Provider Component

export class NotificationCenterProvider extends React.Component<
  TNotificationCenterProviderProps,
  TNotificationCenterProviderState
> {
  public static defaultProps = {
    autoDismissTimeout: 5000,
    autoDismiss: true,
    position: 'TOP_LEFT',
    transitionDuration: 220,
    portalTargetSelector: undefined,
  };

  state: TNotificationCenterProviderState = { notifications: [] };

  hasNotification = (id: string): boolean => {
    if (!this.state.notifications.length) {
      return false;
    }

    return Boolean(
      this.state.notifications.filter((item) => item.id === id).length,
    );
  };

  onDismiss =
    (id: string, cb: Callback = nullFn) =>
    () => {
      cb(id);
      this.remove(id);
    };

  add = (
    content: TNotificationContent,
    options: TNotificationOptions,
    cb: Callback = nullFn,
  ) => {
    const id = options.id ? options.id : generateUID();
    const callback = () => cb(id);

    if (this.hasNotification(id)) {
      return;
    }

    this.setState((state) => {
      const notification = { content, id, ...options };

      const notifications = [...state.notifications, notification];

      return { notifications };
    }, callback);

    return id;
  };

  showSuccess = (message: string) => {
    this.add(
      { title: 'SUCCESS', description: message },
      { variant: 'SUCCESS' },
    );
  };

  showError = (message: string) => {
    this.add({ title: 'ERROR', description: message }, { variant: 'ERROR' });
  };

  remove = (id: string, cb: Callback = nullFn) => {
    const callback = () => cb(id);

    if (!this.hasNotification(id)) {
      return;
    }

    this.setState((state) => {
      const notifications = state.notifications.filter(
        (item) => item.id !== id,
      );
      return { notifications };
    }, callback);
  };

  render() {
    const {
      autoDismiss: inheritedAutoDismiss,
      autoDismissTimeout = 2000,
      children,
      position,
      portalTargetSelector,
      transitionDuration = 220,
    } = this.props;
    const { add, remove, showError, showSuccess } = this;
    const notifications = Object.freeze(this.state.notifications);
    const hasNotifications = Boolean(notifications.length);
    const nodeRef = createRef<HTMLDivElement>();

    const portalTarget = canUseDOM
      ? portalTargetSelector
        ? document.querySelector(portalTargetSelector)
        : document.body
      : null;

    return (
      <Provider
        value={{
          showSuccess: showSuccess,
          showError: showError,
          addNotification: add,
          removeNotification: remove,
          notificationList: notifications,
        }}
      >
        {children}

        {portalTarget ? (
          createPortal(
            <NotificationContainer
              position={position}
              hasNotification={hasNotifications}
            >
              <TransitionGroup component={null}>
                {notifications.map(
                  ({
                    variant,
                    autoDismiss,
                    content,
                    id,
                    onDismiss,
                    ...unknownConsumerProps
                  }) => (
                    <Transition
                      nodeRef={nodeRef}
                      appear
                      key={id}
                      mountOnEnter
                      timeout={transitionDuration}
                      addEndListener={() => {}}
                      unmountOnExit
                    >
                      {(transitionState) => (
                        <div ref={nodeRef}>
                          <NotificationController
                            variant={variant || 'SUCCESS'}
                            autoDismiss={
                              autoDismiss !== undefined
                                ? autoDismiss
                                : inheritedAutoDismiss
                            }
                            autoDismissTimeout={autoDismissTimeout}
                            key={id}
                            onDismiss={this.onDismiss(id, onDismiss)}
                            position={position}
                            transitionDuration={transitionDuration}
                            transitionState={transitionState}
                            content={content}
                            {...unknownConsumerProps}
                          />
                        </div>
                      )}
                    </Transition>
                  ),
                )}
              </TransitionGroup>
            </NotificationContainer>,
            portalTarget,
          )
        ) : (
          <NotificationContainer
            position={position}
            hasNotification={hasNotifications}
          >
            {children}
          </NotificationContainer>
        )}
      </Provider>
    );
  }
}

// !SECTION

export const NotificationContext = ({
  children,
}: TNotificationCenterConsumerProps) => (
  <Consumer>
    {(context) => children(context as INotificaitonCenterContext)}
  </Consumer>
);

export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext);

  if (!context) {
    throw Error(
      'This hook must be used inside of a NotificationCenter component',
    );
  }

  return context;
};
